// @flow
import * as React from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';

import { withFormik as withFormikOriginal } from 'formik';

import {
  pick, isEmpty, get, flowRight
} from 'lodash';

import { HoneypotField } from '@components';

import type { FormikProps, FormikOptions, Entry } from './types';

const initialValues = ({ reduxInitialValuesKey }: FormikOptions) => ({
  url,
  [reduxInitialValuesKey]: initialVals
}: FormikProps): {} => {
  let entry: ?Entry;

  if (!initialVals) return {};

  if (Array.isArray(initialVals.data)) {
    entry = initialVals.data.find(
      (_entry: { id: string }): boolean => _entry.id === url?.query?.id
    );
  } else {
    entry = initialVals.data;
  }

  if (!entry || isEmpty(entry)) return {};

  return entry.attributes;
};

const mapPropsToValues = (options: FormikOptions) => (props: FormikProps): {} => ({
  ...pick(
    {
      ...props,
      ...initialValues(options)(props)
    },
    options.fieldNames
  ),
  ...(options.mapPropsToValues ? options.mapPropsToValues(props) : {})
});

const withFormikAdapated = (options: FormikOptions) => (WrappedComponent: any) => {
  const WithFormikAdapated = (props: FormikProps) => {
    const { initialValues, resetForm, setSubmitting }: FormikProps = props;
    const previousProps = React.useRef();
    const [hiddenFieldValue, setHiddenFieldValue] = React.useState<string>('');

    React.useEffect(() => {
      const { setStatus, setErrors }: FormikProps = props;

      const errorKey = typeof options.reduxErrorKey === 'function'
        ? options.reduxErrorKey(props)
        : options.reduxErrorKey;

      const previousError = get(previousProps.current, errorKey);
      const nextError = get(props, errorKey);

      if (nextError === previousError) {
        return;
      }

      setSubmitting(false);
      setStatus(nextError);
      setErrors(nextError);

      previousProps.current = props;
    }, [props]);

    const { [options.reduxInitialValuesKey]: reduxInitialValues } = props;

    React.useEffect(() => {
      if (!options.enableReduxReinitialize) return;

      if (
        reduxInitialValues?.data?.attributes
        && reduxInitialValues?.data?.attributes !== initialValues
      ) {
        setTimeout(() => {
          resetForm(reduxInitialValues?.data?.attributes);
        }, 0);
      }
    }, [reduxInitialValues]);

    const { onSubmit: noopSubmit, values } = props;

    const handleSubmit = (event) => {
      event.preventDefault();

      if (hiddenFieldValue) return;
      if (props.errors) return;

      if (options.formSubmit) {
        options.handleSubmit(values, { props });
      } else {
        noopSubmit();
      }
    };

    return (
      <form onSubmit={handleSubmit}>
        <HoneypotField value={hiddenFieldValue} onChange={setHiddenFieldValue} />
        <WrappedComponent {...props} />
      </form>
    );
  };

  hoistNonReactStatic(WithFormikAdapated, WrappedComponent);

  return WithFormikAdapated;
};

export default (options: FormikOptions) => (WrappedComponent: any) =>
  flowRight(
    withFormikOriginal({
      ...options,
      mapPropsToValues: mapPropsToValues(options)
    }),
    withFormikAdapated(options)
  )(WrappedComponent);
